import type { ReactElement } from 'react';
import { useChatContext } from '@/contexts/chat/context';
import type { ChatGroupMember } from '@/contexts/chat/scripts/hooks/useChatGroup/types';
import type { Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import type { ChatMessage } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import { useGroupMessageReaders } from '@/contexts/chat/scripts/hooks/useChatGroup/hooks';
import { membersApis } from '@/configs/apis/groups/members';
import styles from './index.module.scss';
import MembersList from './members-list';

export interface GroupMessageReaderProps {
  chat: Chat;
  message: ChatMessage;
  children: ReactElement;
}

export function GroupMessageReader({
  chat,
  message,
  children,
}: GroupMessageReaderProps) {
  const { chatGroup } = useChatContext();
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<ChatGroupMember[]>([]);
  const { readerIds, receiverIds } = useGroupMessageReaders({ message });

  const membersReadInfo = useMemo(() => {
    const readerMembers: ChatGroupMember[] = [];
    const unReaderMembers: ChatGroupMember[] = [];

    members.forEach((member) => {
      if (readerIds.includes(member.user_id)) {
        readerMembers.push(member);
      } else {
        unReaderMembers.push(member);
      }
    });
    return { readerMembers, unReaderMembers };
  }, [members, readerIds]);

  const onOpenChange = useCallback(
    async (isOpen: boolean) => {
      if (isOpen && !members.length && !loading) {
        setLoading(true);
        try {
          const members = await chatGroup.getMembers({
            group_id: chat.chat_id,
            offset: 0,
            limit: -1,
          });

          const membersMap = members.reduce(
            (m, member) => {
              m[member.user_id] = member;
              return m;
            },
            {} as Record<ChatGroupMember['user_id'], ChatGroupMember>
          );

          const notfoundMemberIds: ChatGroupMember['user_id'][] = [];

          receiverIds.forEach((id) => {
            const member = membersMap[id];
            if (!member) {
              notfoundMemberIds.push(id);
            }
          });

          if (notfoundMemberIds.length) {
            const users = await membersApis.getUsersByUserId(notfoundMemberIds);
            users.forEach((user) => {
              membersMap[user.user_id] = user;
            });
          }

          const members_: ChatGroupMember[] = [];

          receiverIds.forEach((id) => {
            const member = membersMap[id];
            if (member) {
              members_.push(member);
            }
          });

          setMembers(members_);
        } finally {
          setLoading(false);
        }
      }
    },
    [chat.chat_id, chatGroup, loading, members.length, receiverIds]
  );

  return (
    <Popover
      placement="topLeft"
      arrow={false}
      trigger="click"
      className={styles.popover}
      classNames={{ root: styles.popoverOverlay }}
      content={
        <div className={styles.messageReadWrapper}>
          <MembersList
            title="Read"
            count={membersReadInfo.readerMembers.length}
            members={membersReadInfo.readerMembers}
            loading={loading}
          />
          <MembersList
            title="Unread"
            count={membersReadInfo.unReaderMembers.length}
            members={membersReadInfo.unReaderMembers}
            loading={loading}
          />
        </div>
      }
      onOpenChange={onOpenChange}
    >
      <span>{children}</span>
    </Popover>
  );
}
